import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components/Shell";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, RefreshCw, Trash2, QrCode, KeyRound, Copy, X } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import QRPrintCard from "../../components/QRPrintCard";

export default function Members() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ member_code: "", name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [qrFor, setQrFor] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [resetCustomPw, setResetCustomPw] = useState("");
  const [resetResult, setResetResult] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/members");
      setMembers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        member_code: form.member_code.trim(),
        name: form.name.trim(),
        password: form.password,
        phone: form.phone,
      };
      if (form.email.trim()) body.email = form.email.trim();
      await api.post("/members", body);
      toast.success("Member created");
      setForm({ member_code: "", name: "", email: "", password: "", phone: "" });
      setShow(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const regen = async (id) => {
    if (!window.confirm("Regenerate QR token? The old QR will stop working.")) return;
    try {
      const { data } = await api.post(`/members/${id}/regenerate-qr`);
      toast.success("QR regenerated");
      setMembers((ms) => ms.map((m) => (m.id === id ? { ...m, qr_token: data.qr_token } : m)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this member? Attendance & documents will also be removed.")) return;
    try {
      await api.delete(`/members/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadQR = (member) => {
    const canvas = document.getElementById(`qr-${member.id}`);
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-${member.name.replace(/\s+/g, "_")}.png`;
    a.click();
  };

  const openReset = (m) => {
    setResetFor(m);
    setResetCustomPw("");
    setResetResult(null);
  };
  const closeReset = () => {
    setResetFor(null);
    setResetCustomPw("");
    setResetResult(null);
  };
  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetFor) return;
    setResetBusy(true);
    try {
      const body = resetCustomPw.trim() ? { password: resetCustomPw.trim() } : {};
      const { data } = await api.post(`/members/${resetFor.id}/reset-password`, body);
      setResetResult(data);
      toast.success("Password reset");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setResetBusy(false);
    }
  };
  const copyPw = async () => {
    if (!resetResult?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temporary_password);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  return (
    <div data-testid="members-page">
      <PageHeader
        eyebrow="Org Admin"
        title="Members"
        actions={
          <button
            onClick={() => setShow((v) => !v)}
            className="px-4 py-2 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] transition-colors flex items-center gap-2"
            data-testid="open-create-member-btn"
          >
            <Plus className="w-4 h-4" /> New member
          </button>
        }
      />

      {show && (
        <form
          onSubmit={create}
          className="mb-8 border border-[#D2D8D4] rounded-md bg-white p-5 grid md:grid-cols-6 gap-3 items-end"
          data-testid="create-member-form"
        >
          <Field label="Member ID">
            <input required value={form.member_code} onChange={(e) => setForm({ ...form, member_code: e.target.value })}
              placeholder="e.g. 2024-0001"
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="member-code" />
          </Field>
          <Field label="Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="member-name" />
          </Field>
          <Field label="Email (optional)">
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="member-email" />
          </Field>
          <Field label="Password">
            <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="member-password" />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="member-phone" />
          </Field>
          <button type="submit" disabled={busy}
            className="px-4 py-2 rounded-md bg-[#0f1a14] text-white font-semibold disabled:opacity-60 hover:bg-[#1f2a24] transition-colors"
            data-testid="submit-member">
            Create
          </button>
        </form>
      )}

      <div className="border border-[#E4E4E7] bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-[#F7F7F8] border-b border-[#E4E4E7]">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Member ID</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Name</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Email</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Phone</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No members yet.</td></tr>
            )}
            {members.map((m) => (
              <tr key={m.id} className="border-b border-[#E4E4E7] hover:bg-[#F7F7F8]">
                <td className="px-4 py-3 mono font-semibold text-[#05126E]" data-testid={`member-code-${m.id}`}>
                  {m.member_code || "—"}
                </td>
                <td className="px-4 py-3 font-semibold">{m.name}</td>
                <td className="px-4 py-3 mono">{m.email || "—"}</td>
                <td className="px-4 py-3">{m.phone || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setQrFor(qrFor?.id === m.id ? null : m)}
                      className="p-1.5 rounded-md border border-[#D2D8D4] hover:border-[#0f1a14] hover:bg-[#F4F6F5] transition-colors"
                      data-testid={`qr-btn-${m.id}`} title="Show QR">
                      <QrCode className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => regen(m.id)}
                      className="p-1.5 rounded-md border border-[#D2D8D4] hover:border-[#0f1a14] hover:bg-[#F4F6F5] transition-colors"
                      data-testid={`regen-btn-${m.id}`} title="Regenerate QR">
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => openReset(m)}
                      className="p-1.5 rounded-md border border-[#D2D8D4] hover:border-[#0f1a14] hover:bg-[#F4F6F5] transition-colors"
                      data-testid={`reset-pw-btn-${m.id}`} title="Reset password">
                      <KeyRound className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(m.id)}
                      className="p-1.5 border border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white"
                      data-testid={`del-btn-${m.id}`} title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {qrFor && (
        <div
          className="fixed inset-0 bg-black/60 grid place-items-center z-50 p-4"
          onClick={() => setQrFor(null)}
          data-testid="qr-modal"
        >
          <div className="bg-white rounded-lg p-5 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <QRPrintCard
              qrCanvasId={`qr-${qrFor.id}`}
              qrValue={qrFor.qr_token}
              memberName={qrFor.name}
              memberCode={qrFor.member_code}
              organizationName={user?.organization_name || ""}
              size={220}
              QRComponent={QRCodeCanvas}
            />
            <div className="mt-4 flex gap-2 no-print">
              <button
                onClick={() => downloadQR(qrFor)}
                className="flex-1 px-4 py-2 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] transition-colors"
                data-testid="download-qr-btn"
              >
                Download PNG
              </button>
              <button
                onClick={() => window.print()}
                className="flex-1 px-4 py-2 rounded-md border border-[#0f1a14] font-semibold hover:bg-[#0f1a14] hover:text-white transition-colors"
                data-testid="print-qr-btn"
              >
                Print
              </button>
              <button
                onClick={() => setQrFor(null)}
                className="px-4 py-2 rounded-md border border-[#E3E6E4] font-semibold hover:bg-[#F4F6F5]"
              >
                Close
              </button>
            </div>
            <div className="mt-3 text-[10px] mono text-[#5a6663] break-all no-print">
              {qrFor.qr_token}
            </div>
          </div>
        </div>
      )}

      {resetFor && (
        <div
          className="fixed inset-0 bg-black/60 grid place-items-center z-50 p-4"
          onClick={closeReset}
          data-testid="reset-pw-modal"
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#05126E]">
                  Reset password
                </div>
                <div className="text-xl font-extrabold text-[#0f1a14] mt-0.5">
                  {resetFor.name}
                </div>
                <div className="text-xs text-[#5a6663] mono">{resetFor.email || resetFor.member_code}</div>
              </div>
              <button
                onClick={closeReset}
                className="p-1.5 rounded-md hover:bg-[#F4F6F5]"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {!resetResult ? (
              <form onSubmit={submitReset} className="mt-5 space-y-3">
                <div>
                  <label className="text-xs font-semibold tracking-wider text-[#0f1a14]">
                    New password (optional)
                  </label>
                  <input
                    type="text"
                    value={resetCustomPw}
                    onChange={(e) => setResetCustomPw(e.target.value)}
                    placeholder="Leave blank to auto-generate"
                    className="mt-1 w-full px-3 py-2.5 rounded-md border border-[#D2D8D4] focus:border-[#05126E] focus:ring-2 focus:ring-[#05126E]/20 outline-none"
                    data-testid="reset-pw-input"
                  />
                  <div className="text-[11px] text-[#5a6663] mt-1">
                    Min 6 chars. Leave empty for a strong auto-generated temporary password.
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={resetBusy}
                    className="flex-1 px-4 py-2.5 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] disabled:opacity-60 transition-colors"
                    data-testid="reset-pw-submit"
                  >
                    {resetBusy ? "Resetting…" : "Reset password"}
                  </button>
                  <button
                    type="button"
                    onClick={closeReset}
                    className="px-4 py-2.5 rounded-md border border-[#E3E6E4] font-semibold hover:bg-[#F4F6F5]"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="mt-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5a6663]">
                  {resetResult.generated ? "Temporary password" : "Password set"}
                </div>
                <div
                  className="mt-2 flex items-center gap-2 p-3 rounded-md border border-[#D2D8D4] bg-[#F4F6F5]"
                  data-testid="reset-pw-result"
                >
                  <code className="mono text-base font-bold text-[#0f1a14] break-all flex-1">
                    {resetResult.temporary_password}
                  </code>
                  <button
                    onClick={copyPw}
                    className="p-2 rounded-md border border-[#D2D8D4] hover:border-[#0f1a14] hover:bg-white transition-colors"
                    data-testid="copy-pw-btn"
                    title="Copy"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-[11px] text-[#5a6663] mt-2">
                  Share this with the member through a trusted channel. They should
                  change it on first login.
                </div>
                <button
                  onClick={closeReset}
                  className="mt-4 w-full px-4 py-2.5 rounded-md bg-[#0f1a14] text-white font-semibold hover:bg-[#1f2a24]"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
